import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  FileText, 
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertTriangle,
  Target
} from 'lucide-react';

import { statisticsData } from '../../data/mockData';

// Données importées depuis mockData
const statistics = statisticsData;

const Statistics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('mois');
  const [selectedView, setSelectedView] = useState('synthese');
  
  const currentStats = statisticsData.periodes[selectedPeriod];
  const totalDossiers = currentStats.dossiersCreees + currentStats.dossiersEnCours + currentStats.dossiersOuverts;

  const handleExport = (format) => {
    // Ici on ferait l'export réel
    alert(`Export ${format} en cours...`);
  };

  const getPerformanceColor = (value, type) => {
    if (type === 'taux') {
      return value >= 95 ? 'text-atl-green-600' : value >= 90 ? 'text-yellow-600' : 'text-red-600';
    } else if (type === 'duree') {
      return value <= 7 ? 'text-atl-green-600' : value <= 10 ? 'text-yellow-600' : 'text-red-600';
    }
    return 'text-port-gray-600';
  };

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-port-gray-900">Statistiques</h1>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Sélection de période */}
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-port-gray-500" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="form-select"
            >
              <option value="jour">Aujourd'hui</option>
              <option value="semaine">Cette semaine</option>
              <option value="mois">Ce mois</option>
              <option value="trimestre">Ce trimestre</option>
              <option value="annee">Cette année</option>
            </select>
          </div>
          
          {/* Boutons d'export */}
          <div className="flex space-x-2">
            <button
              onClick={() => handleExport('excel')}
              className="btn btn-secondary btn-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="btn btn-secondary btn-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
          </div>
          
          <button className="btn btn-primary btn-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Onglets de vues */}
      <div className="border-b border-port-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setSelectedView('synthese')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              selectedView === 'synthese'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Vue Synthèse
          </button>
          <button
            onClick={() => setSelectedView('enqueteurs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              selectedView === 'enqueteurs'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Performance Enquêteurs
          </button>
        </nav>
      </div>

      {/* Vue Synthèse */}
      {selectedView === 'synthese' && (
        <div className="space-y-6">
          {/* Indicateurs principaux */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="p-3 bg-atl-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-atl-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-port-gray-600">Dossiers Créés</p>
                    <p className="text-2xl font-bold text-port-gray-900">{currentStats.dossiersCreees}</p>
                    <p className="text-xs text-port-gray-500">{currentStats.label}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="p-3 bg-atl-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-atl-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-port-gray-600">Dossiers Clôturés</p>
                    <p className="text-2xl font-bold text-atl-green-600">{currentStats.dossiersCloturs}</p>
                    <p className="text-xs text-port-gray-500">
                      {((currentStats.dossiersCloturs / totalDossiers) * 100).toFixed(1)}% du total
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-port-gray-600">En Cours</p>
                    <p className="text-2xl font-bold text-yellow-600">{currentStats.dossiersEnCours}</p>
                    <p className="text-xs text-port-gray-500">
                      {((currentStats.dossiersEnCours / totalDossiers) * 100).toFixed(1)}% du total
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-port-gray-600">Non Attribués</p>
                    <p className="text-2xl font-bold text-red-600">{currentStats.dossiersOuverts}</p>
                    <p className="text-xs text-port-gray-500">
                      {((currentStats.dossiersOuverts / totalDossiers) * 100).toFixed(1)}% du total
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Répartition par type */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium">Répartition par Type de Sinistre</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {Object.entries(statisticsData.repartitionTypes).map(([type, count]) => {
                    const total = Object.values(statisticsData.repartitionTypes).reduce((a, b) => a + b, 0);
                    const percentage = (count / total) * 100;
                    return (
                      <div key={type}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-port-gray-700">{type}</span>
                          <span className="text-sm text-port-gray-500">{count} ({percentage.toFixed(1)}%)</span>
                        </div>
                        <div className="w-full bg-port-gray-200 rounded-full h-2">
                          <div 
                            className="bg-atl-blue-600 h-2 rounded-full" 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Évolution mensuelle */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium">Évolution Mensuelle</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {statisticsData.evolutionMensuelle.map(data => (
                    <div key={data.mois} className="flex items-center space-x-4">
                      <div className="w-12 text-sm font-medium text-port-gray-700">{data.mois}</div>
                      <div className="flex-1">
                        <div className="flex space-x-2">
                          <div className="flex-1">
                            <div className="flex justify-between text-xs mb-1">
                              <span>Créés</span>
                              <span>{data.crees}</span>
                            </div>
                            <div className="w-full bg-port-gray-200 rounded h-2">
                              <div 
                                className="bg-atl-blue-500 h-2 rounded" 
                                style={{ width: `${(data.crees / 60) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between text-xs mb-1">
                              <span>Clôturés</span>
                              <span>{data.cloturs}</span>
                            </div>
                            <div className="w-full bg-port-gray-200 rounded h-2">
                              <div 
                                className="bg-atl-green-500 h-2 rounded" 
                                style={{ width: `${(data.cloturs / 60) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Vue Performance Enquêteurs */}
      {selectedView === 'enqueteurs' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {statisticsData.enqueteurs.map(enqueteur => (
              <div key={enqueteur.id} className="card">
                <div className="card-header">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-atl-blue-100 rounded-full flex items-center justify-center mr-3">
                      <Users className="h-5 w-5 text-atl-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-port-gray-900">{enqueteur.nom}</h3>
                      <p className="text-sm text-port-gray-500">
                        {enqueteur.specialites.join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-port-gray-600">Dossiers attribués</span>
                      <span className="font-medium text-port-gray-900">{enqueteur.dossiersAttribues}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-port-gray-600">Dossiers refusés</span>
                      <span className="font-medium text-red-600">{enqueteur.dossiersRefuses}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-port-gray-600">En cours</span>
                      <span className="font-medium text-yellow-600">{enqueteur.dossiersEnCours}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-port-gray-600">Clôturés</span>
                      <span className="font-medium text-atl-green-600">{enqueteur.dossiersCloturs}</span>
                    </div>
                    
                    <div className="border-t border-port-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-port-gray-600">Durée moyenne</span>
                        <span className={`font-medium ${getPerformanceColor(enqueteur.dureeTraitementMoyenne, 'duree')}`}>
                          {enqueteur.dureeTraitementMoyenne} jours
                        </span>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tableau comparatif */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">Comparatif Performance Enquêteurs</h3>
            </div>
            <div className="card-body">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Enquêteur</th>
                      <th>Attribués</th>
                      <th>Refusés</th>
                      <th>En cours</th>
                      <th>Clôturés</th>
                      <th>Durée moyenne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statisticsData.enqueteurs.map(enqueteur => (
                      <tr key={enqueteur.id}>
                        <td className="font-medium">{enqueteur.nom}</td>
                        <td>{enqueteur.dossiersAttribues}</td>
                        <td className="text-red-600">{enqueteur.dossiersRefuses}</td>
                        <td className="text-yellow-600">{enqueteur.dossiersEnCours}</td>
                        <td className="text-atl-green-600">{enqueteur.dossiersCloturs}</td>
                        <td className={getPerformanceColor(enqueteur.dureeTraitementMoyenne, 'duree')}>
                          {enqueteur.dureeTraitementMoyenne} jours
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Statistics;
